/**
 * What the room is actually paying, while it pays it.
 *
 * A pre-draft dollar value is a prediction, and an auction is the one format
 * that tells you how wrong it is *before* you have to act on it. Every closed
 * bid is a data point: the room paid $62 for a player this board has at $53,
 * and it paid it with money that no longer exists. Both halves of that matter,
 * and only the second half is arithmetic you cannot argue with.
 *
 * The number to draft off is not what the room has overpaid — it is what is
 * left. An auction is a closed system: `teams x budget` dollars chase
 * `teams x roster spots` players and every dollar gets spent, so if the first
 * thirty players go for 20% over this board's prices, the remaining players
 * must go for *less* than their prices. Money spent early is not money lost to
 * the market, it is money removed from everyone else's ability to outbid you
 * later. That is why `rate` below is forward-looking — dollars still in the
 * room over value still on the board — rather than a running tally of how much
 * the room has overpaid. The tally is the past; the rate is what your next bid
 * is competing against.
 *
 * Read it as a multiplier on this board's prices: 1.15 means the players you
 * still want will cost about 15% over their listed number, so bid accordingly
 * or you will end the night with unspent money and an empty bench slot. 0.85
 * means the room has spent itself out and the rest of the board is on sale.
 *
 * The same reading holds one level down, per position, but only if it is built
 * carefully — see `positionInflation` below for why the obvious construction
 * of it is wrong.
 */

/** Below this many closed bids the rate is noise — one aggressive owner. */
export const MIN_BIDS_FOR_RATE = 8;

/**
 * A rate this far from 1.0 is worth changing your bidding over.
 *
 * Inside it the board's prices stand as written; outside it they want the
 * multiplier applied before you decide what a player is worth tonight.
 */
export const RATE_NOISE = 0.08;

/**
 * A position this far off the room's own rate is worth naming out loud.
 *
 * Tighter than `RATE_NOISE` because a positional number is already measured
 * against the room rather than against 1.0: the room-wide drift it would
 * otherwise be picking up has been divided out, so what is left moves less and
 * a smaller move means more.
 */
export const POSITION_NOISE = 0.05;

/**
 * How much closed board value a position needs before its rate is its own.
 *
 * Half weight at this many dollars of board value bought, and the shrink is
 * smooth rather than a cutoff — a position with two bids in should nudge the
 * price, not swing it, and should not have to cross a line to start counting.
 * Quoted as a share of one team's budget so it scales with the league: at $200
 * a position is at half weight once about $100 of board value has left it,
 * which is roughly the first four bids that matter.
 */
export const HEAT_HALF_WEIGHT_SHARE = 0.5;

/**
 * The furthest a position may sit from the room before the number is a guess.
 *
 * One $80 bid on a $30 player is an owner losing his head, not a market, and
 * without a clamp it would price every remaining player at that position off
 * that one moment.
 */
export const MAX_HEAT_SWING = 0.35;

/**
 * The winning bid on a Sleeper auction pick.
 *
 * Sleeper puts it in `metadata.amount` as a string, and only on auction
 * drafts — a snake pick has no amount and must come back null rather than 0,
 * because a 0 would drag every average it touches toward zero. Takes either a
 * raw Sleeper pick or a draft-log entry that already carries the parsed
 * `amount`, so the sync layer and the maths below agree on one definition.
 */
export const pickAmount = (pick) => {
  const raw = pick?.amount ?? pick?.metadata?.amount;
  if (raw == null || raw === '') return null;
  const amount = Number(raw);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
};

/**
 * How the auction is running, from the bids that have closed.
 *
 * `board` is the priced board; `draftLog` is what has been bought, each entry
 * carrying the `amount` paid and the `id` of the row it landed on. Rows the
 * log could not be matched to are simply absent from the value side, so they
 * are dropped from the spend side too — comparing dollars paid against a value
 * we do not have would report inflation that is really a name-matching miss.
 */
export const auctionInflation = (board, draftLog, { teams, budget, rosterSize } = {}) => {
  const teamCount = Number(teams) > 0 ? Number(teams) : 12;
  const cap = Number(budget) > 0 ? Number(budget) : 200;
  const spots = Number(rosterSize) > 0 ? Number(rosterSize) : 15;

  const pricedRows = board.filter((row) => row.auction != null);
  const priceById = new Map(pricedRows.map((row) => [row.id, row.auction]));
  const positionById = new Map(pricedRows.map((row) => [row.id, row.position || null]));

  const bought = (draftLog || []).filter((entry) => pickAmount(entry) != null);
  const priced = bought.filter((entry) => priceById.has(entry.id));

  const spent = bought.reduce((sum, entry) => sum + pickAmount(entry), 0);
  const spentOnPriced = priced.reduce((sum, entry) => sum + pickAmount(entry), 0);
  const valueOfGone = priced.reduce((sum, entry) => sum + priceById.get(entry.id), 0);

  const boughtIds = new Set(bought.map((entry) => entry.id));
  const spotsLeft = Math.max(0, teamCount * spots - bought.length);

  // Only the players who can still be bought, and only as many of them as
  // there are seats left: an auction stops when the rosters fill, so the value
  // still on the board is the best `spotsLeft` of it and not the long tail
  // underneath, which nobody will ever bid on.
  const stillBiddable = pricedRows
    .filter((row) => !boughtIds.has(row.id))
    .sort((a, b) => b.auction - a.auction)
    .slice(0, spotsLeft);
  const valueLeft = stillBiddable.reduce((sum, row) => sum + row.auction, 0);

  const moneyLeft = teamCount * cap - spent;

  const paidRate = valueOfGone > 0 ? spentOnPriced / valueOfGone : null;
  const rate =
    bought.length >= MIN_BIDS_FOR_RATE && valueLeft > 0 && moneyLeft > 0
      ? moneyLeft / valueLeft
      : null;

  return {
    bids: bought.length,
    spent,
    moneyLeft,
    spotsLeft,
    valueLeft,
    /** What the room has paid per dollar of this board's value, so far. */
    paidRate,
    /**
     * The multiplier to put on every remaining price. Null until enough bids
     * have closed to mean anything, and null once the money or the board runs
     * out — at which point there is nothing left to price.
     */
    rate,
    /** The same question asked one level down. Empty until `rate` exists. */
    positions:
      rate == null
        ? []
        : positionInflation({
            priced,
            priceById,
            positionById,
            stillBiddable,
            paidRate,
            moneyLeft,
            rate,
            cap,
          }),
  };
};

/**
 * The same money-over-value question, asked position by position.
 *
 * The obvious construction — money left for running backs over running back
 * value left — has no numerator. Nobody's budget is earmarked by position; the
 * same dollar bids on a back and a receiver, and which one it lands on is the
 * thing being measured. So the positional read has to be built out of what the
 * room has actually paid, and that runs into a second trap: for the first
 * thirty bids of any auction the room overpays for *everything*, because the
 * players who go first are the ones worth overpaying for. A position that
 * simply went early looks hot when it is only early.
 *
 * Both problems have the same fix — measure a position against the room rather
 * than against the board:
 *
 *   1. `paidRate` per position, over the same stretch as the room's own, so
 *      the room-wide drift divides out and what is left is the position's.
 *   2. Shrink that toward the room by how much board value has actually closed
 *      there, so two bids nudge and twenty decide.
 *   3. Rescale every position together until the remaining prices add back up
 *      to the money still in the room.
 *
 * Step 3 is what makes the whole thing honest. The room's money is finite, so
 * a position running hot is not a free-standing fact — it is money that will
 * not be spent somewhere else, and "backs are going 20% over" has to come out
 * as "receivers are on sale" or it is not arithmetic, it is a vibe. Rescaling
 * enforces exactly that, and it degrades correctly: with every position
 * neutral the scale factor is `moneyLeft / valueLeft` and every row prices at
 * the room rate, which is the number this module already published.
 *
 * What is deliberately *not* folded in is scarcity — how much of a position's
 * value is already gone. It is reported, because a room that has bought every
 * startable tight end is a room about to bid strangely on the next one, but
 * feeding it into the price would count the same fact twice: a position whose
 * good players are gone is usually a position the room has already been paying
 * up for, which the paid rate has counted once already.
 */
const positionInflation = ({
  priced,
  priceById,
  positionById,
  stillBiddable,
  paidRate,
  moneyLeft,
  rate,
  cap,
}) => {
  const bucket = (map, position) => {
    if (!map.has(position)) {
      map.set(position, { position, bids: 0, spent: 0, valueGone: 0, valueLeft: 0, spotsLeft: 0 });
    }
    return map.get(position);
  };

  const byPosition = new Map();

  for (const entry of priced) {
    const position = positionById.get(entry.id);
    if (!position) continue;
    const row = bucket(byPosition, position);
    row.bids += 1;
    row.spent += pickAmount(entry);
    row.valueGone += priceById.get(entry.id);
  }

  for (const player of stillBiddable) {
    if (!player.position) continue;
    const row = bucket(byPosition, player.position);
    row.valueLeft += player.auction;
    row.spotsLeft += 1;
  }

  const half = Math.max(1, cap * HEAT_HALF_WEIGHT_SHARE);
  const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

  const rows = [...byPosition.values()].map((row) => {
    const posPaidRate = row.valueGone > 0 ? row.spent / row.valueGone : null;
    // Weight by dollars of board value closed, not by bid count: one $60 back
    // tells you more about how backs are being priced than six $2 ones do.
    const weight = row.valueGone / (row.valueGone + half);
    const raw =
      posPaidRate != null && paidRate > 0
        ? clamp(posPaidRate / paidRate, 1 - MAX_HEAT_SWING, 1 + MAX_HEAT_SWING)
        : 1;
    return {
      ...row,
      paidRate: posPaidRate,
      /** How much of this position's priced value has already been bought. */
      goneShare: row.valueGone + row.valueLeft > 0
        ? row.valueGone / (row.valueGone + row.valueLeft)
        : null,
      heat: 1 + (raw - 1) * weight,
    };
  });

  // The rescale. `heat` is only a relative shape; this is what turns it into
  // dollars, by insisting the shape still adds up to the money in the room.
  const weighted = rows.reduce((sum, row) => sum + row.valueLeft * row.heat, 0);
  const scale = weighted > 0 ? moneyLeft / weighted : rate;

  return rows
    .map((row) => ({
      ...row,
      /** The multiplier for a remaining price at this position. */
      rate: scale * row.heat,
      /** And the same thing said against the room, which is how it reads. */
      vsRoom: rate > 0 ? (scale * row.heat) / rate : 1,
    }))
    .sort((a, b) => b.vsRoom - a.vsRoom);
};

/**
 * The multiplier for one row: its position's if the position has one, else the
 * room's. Positions the board never priced (and a snake draft's null state)
 * fall back rather than disappearing.
 */
export const positionRate = (state, position) => {
  if (!state || state.rate == null) return null;
  const row = state.positions?.find((entry) => entry.position === position);
  return row ? row.rate : state.rate;
};

/** The positions far enough off the room to be worth naming, hottest first. */
export const positionOutliers = (state) =>
  (state?.positions || []).filter((row) => Math.abs(row.vsRoom - 1) >= POSITION_NOISE);

/** A remaining price with the room's current inflation applied. */
export const inflatedPrice = (auction, rate) => {
  if (auction == null) return null;
  if (rate == null) return auction;
  return Math.max(1, Math.round(auction * rate));
};

/**
 * Budget, spots and max bid for every team in the auction.
 *
 * `maxBid` is the one number an auction room reliably gets wrong in its head:
 * you cannot spend your last dollars on one player, because every empty roster
 * spot behind him still costs a dollar to fill. A manager with $40 and four
 * holes can bid $37, not $40 — and knowing that about the other eleven owners
 * tells you exactly who can still come over the top of you.
 */
export const teamBudgets = (draftLog, { teams, budget, rosterSize, teamNames } = {}) => {
  const teamCount = Number(teams) > 0 ? Number(teams) : 12;
  const cap = Number(budget) > 0 ? Number(budget) : 200;
  const spots = Number(rosterSize) > 0 ? Number(rosterSize) : 15;

  const byRoster = new Map();
  for (const entry of draftLog || []) {
    const amount = pickAmount(entry);
    if (amount == null || entry.rosterId == null) continue;
    const key = String(entry.rosterId);
    const team = byRoster.get(key) || { rosterId: key, teamName: entry.teamName, spent: 0, bought: 0 };
    team.spent += amount;
    team.bought += 1;
    byRoster.set(key, team);
  }

  // Every roster in the league, not only the ones that have bought something —
  // an owner sitting on a full budget is the most important row in this table.
  const rosterIds = new Set(byRoster.keys());
  for (const [rosterId] of teamNames || []) rosterIds.add(String(rosterId));

  return [...rosterIds]
    .map((rosterId) => {
      const team = byRoster.get(rosterId) || { rosterId, spent: 0, bought: 0 };
      const left = cap - team.spent;
      const spotsLeft = Math.max(0, spots - team.bought);
      return {
        rosterId,
        teamName: teamNames?.get(rosterId) || team.teamName || `Roster ${rosterId}`,
        spent: team.spent,
        bought: team.bought,
        left,
        spotsLeft,
        // Hold back a dollar for every spot after this one.
        maxBid: spotsLeft > 0 ? Math.max(0, left - (spotsLeft - 1)) : 0,
      };
    })
    .sort((a, b) => b.maxBid - a.maxBid)
    .slice(0, Math.max(teamCount, byRoster.size));
};

export const formatInflationTooltip = (state) => {
  if (!state || state.rate == null) {
    return (
      `${state?.bids || 0} bid${state?.bids === 1 ? '' : 's'} closed — not enough ` +
      `yet to say how the room is pricing. The board's numbers stand as written.`
    );
  }

  const pct = Math.round((state.rate - 1) * 100);
  const lines = [
    `$${state.moneyLeft} still in the room, chasing $${state.valueLeft} of value ` +
      `across ${state.spotsLeft} open roster spots.`,
  ];

  if (Math.abs(state.rate - 1) < RATE_NOISE) {
    lines.push('The room is paying about what this board says. Bid the listed price.');
  } else if (pct > 0) {
    lines.push(
      `Everything left will go for roughly ${pct}% over its listed price. Bid up ` +
        `to that or you will finish the night holding money you cannot spend.`,
    );
  } else {
    lines.push(
      `The room has spent itself out — what is left should go for about ` +
        `${Math.abs(pct)}% under its listed price. Sit on your money and let the ` +
        `board come to you.`,
    );
  }

  if (state.paidRate != null) {
    lines.push(
      `So far it has paid $${state.paidRate.toFixed(2)} per dollar of this board's value.`,
    );
  }
  return lines.join('\n');
};

const signed = (pct) => `${pct > 0 ? '+' : '−'}${Math.abs(pct)}%`;

/**
 * The positional read, in the few characters a draft room will actually read.
 *
 * Only the outliers, and at most three of them: a strip that names all six
 * positions every time is a strip nobody looks at, and the four sitting at the
 * room rate are precisely the ones with nothing to say.
 */
export const formatPositionHeat = (state) => {
  if (!state || state.rate == null) return null;
  const outliers = positionOutliers(state);
  if (!outliers.length) return 'Level across positions';
  const ends = outliers.length > 3 ? [outliers[0], outliers[1], outliers[outliers.length - 1]] : outliers;
  return ends
    .map((row) => `${row.position} ${signed(Math.round((row.vsRoom - 1) * 100))}`)
    .join(' · ');
};

export const formatPositionTooltip = (state) => {
  if (!state || state.rate == null) {
    return 'Not enough closed bids yet to tell one position from another.';
  }

  const lines = [
    'Each position against the room, with the room-wide drift divided out — so',
    'this is the position running hot, not just the position that went early.',
    '',
  ];

  for (const row of state.positions) {
    const pct = Math.round((row.vsRoom - 1) * 100);
    const shape =
      Math.abs(row.vsRoom - 1) < POSITION_NOISE
        ? 'at the room rate'
        : `${signed(pct)} vs the room`;
    const gone =
      row.goneShare != null ? `${Math.round(row.goneShare * 100)}% of its value gone` : 'nothing gone';
    lines.push(
      `${row.position} — ${shape}, so pay about ${Math.round(row.rate * 100)}% of list ` +
        `(${row.bids} bid${row.bids === 1 ? '' : 's'} in, ${gone})`,
    );
  }

  lines.push('');
  lines.push(
    'The money is finite, so these have to net out: a position going over list is',
    'paid for by one going under. Bid the adjusted price, not the listed one.',
  );
  return lines.join('\n');
};

/** The one line a player's price cell needs about why it is not the list price. */
export const formatRowInflationNote = (state, row) => {
  const rate = positionRate(state, row?.position);
  if (rate == null || row?.auction == null) return null;
  const price = inflatedPrice(row.auction, rate);
  if (price === row.auction) return `The room's pricing leaves him at his listed $${row.auction}.`;
  const pos = state.positions?.find((entry) => entry.position === row.position);
  const because =
    pos && Math.abs(pos.vsRoom - 1) >= POSITION_NOISE
      ? `${row.position} is running ${signed(Math.round((pos.vsRoom - 1) * 100))} against the room tonight`
      : 'that is the room-wide rate';
  return `Tonight he is about $${price} — ${because}.`;
};
